import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import EmployeeEditForm from "../../forms/employeeEditForm";
import CustomButton from "../../../../components/lib/customButton";
import Spinner from "../../../../components/lib/spinner";
import Stepper from "../../../../components/lib/stepperHeader";
import { formatCurrency } from "../../../../utils/CurrencyUtils";

const STEP = {
    EMPLOYEE: 0,
    SUMMARY: 1,
};

const steps = [
    { key: STEP.EMPLOYEE, label: "Colaborador" },
    { key: STEP.SUMMARY, label: "Resumo" },
];

const EmployeeEditStepper = ({ employee, onFinish, onCancel, onDirty, isSubmitting }) => {
    const [step, setStep] = useState(STEP.EMPLOYEE);
    const [employeeDraft, setEmployeeDraft] = useState(null);

    useEffect(() => {
        if (!employee) return;
        setStep(STEP.EMPLOYEE);
        setEmployeeDraft(employee);
    }, [employee]);

    const submitActiveForm = () => {
        if (isSubmitting) return;
        document.querySelector("form[data-step-form]")?.requestSubmit();
    };

    const goBack = () => {
        if (isSubmitting) return;

        if (step === STEP.EMPLOYEE) {
            onCancel?.();
            return;
        }

        setStep(s => s - 1);
    };

    const handleFinalSubmit = () => {
        if (isSubmitting) return;
        onFinish?.({ employee: employeeDraft });
    };

    return (
        <div className="flex flex-col gap-6">
            <Stepper steps={steps} currentStep={step} />

            <div className="min-h-[360px]">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                    >
                        {step === STEP.EMPLOYEE && employeeDraft && (
                            <EmployeeEditForm
                                isOpen={true}
                                employee={employeeDraft}
                                onDirty={onDirty}
                                hideFooter
                                onClose={onCancel}
                                onSubmit={(data) => {
                                    setEmployeeDraft(data);
                                    setStep(STEP.SUMMARY);
                                }}
                            />
                        )}

                        {step === STEP.SUMMARY && employeeDraft && (
                            <div className="space-y-5 text-sm">
                                <div className="space-y-1">
                                    <p className="font-semibold text-base">Colaborador</p>

                                    <p><span className="font-medium">Nome:</span> {employeeDraft.name}</p>
                                    <p><span className="font-medium">Email:</span> {employeeDraft.email || "Não informado"}</p>
                                    <p><span className="font-medium">Telefone:</span> {employeeDraft.phone_number || "Não informado"}</p>
                                    <p><span className="font-medium">Valor de Emergência:</span> {formatCurrency(employeeDraft.emergency_value)}</p>
                                    <p><span className="font-medium">Limite de gastos diário:</span> {formatCurrency(employeeDraft.spend_limit)}</p>

                                    {employeeDraft.email !== employee.email && (
                                        <p className="text-blue-600">Email alterado</p>
                                    )}
                                    {employeeDraft.phone_number !== employee.phone_number && (
                                        <p className="text-blue-600">Telefone alterado</p>
                                    )}
                                    {Number(employeeDraft.emergency_value) !== Number(employee.emergency_value) && (
                                        <p className="text-blue-600">Valor de emergência alterado</p>
                                    )}
                                    {Number(employeeDraft.spend_limit) !== Number(employee.spend_limit) && (
                                        <p className="text-blue-600">Limite de gastos diário alterado</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="flex justify-between">
                <CustomButton
                    buttonType={step === STEP.EMPLOYEE ? "cancel" : "confirmLater"}
                    text={step === STEP.EMPLOYEE ? "Cancelar" : "Voltar"}
                    showIcon={false}
                    disabled={isSubmitting}
                    onClick={goBack}
                />

                {step < STEP.SUMMARY && (
                    <CustomButton
                        buttonType="confirm"
                        text="Avançar"
                        showIcon={false}
                        disabled={isSubmitting}
                        onClick={submitActiveForm}
                    />
                )}

                {step === STEP.SUMMARY && (
                    <CustomButton
                        buttonType="confirm"
                        showIcon={false}
                        disabled={isSubmitting}
                        onClick={handleFinalSubmit}
                        text={
                            isSubmitting ? (
                                <>
                                    <span className="ml-2">Salvando</span>
                                    <Spinner />
                                </>
                            ) : (
                                "Salvar alterações"
                            )}
                    />
                )}
            </div>
        </div>
    );
};

export default EmployeeEditStepper;
